import React, { useEffect, useMemo, useState } from "react";
import { api, fmtErr, kg, money, num, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import DateDMY from "@/components/DateDMY";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Lock, PencilLine, Check, X, Plus, Trash2, Layers, Download, FileText, Undo2 } from "lucide-react";
import DeleteConfirm from "@/components/DeleteConfirm";
import ReturnsDialog from "@/components/ReturnsDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const uid = () => Math.random().toString(36).slice(2, 10);

const podBadge = (p) => ({
  Pending: "bg-slate-100 text-slate-700 border-slate-300",
  Received: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Damaged: "bg-rose-50 text-rose-700 border-rose-200",
  Missing: "bg-amber-50 text-amber-700 border-amber-200",
  "Not Required": "bg-slate-100 text-slate-500 border-slate-300 italic",
}[p] || "bg-slate-100 text-slate-700");

const typeBadge = (t) => ({
  transporter: "bg-blue-50 text-blue-700",
  company: "bg-emerald-50 text-emerald-700",
  customer: "bg-violet-50 text-violet-700",
}[t] || "bg-slate-100 text-slate-700");

const returnBadge = (t) => ({
  "Partial Return": "bg-amber-50 text-amber-700 border-amber-200",
  "Full Return": "bg-rose-50 text-rose-700 border-rose-200",
}[t] || "bg-slate-50 text-slate-500 border-slate-200");

const batchesSummary = (bs) => {
  if (!bs || bs.length === 0) return "-";
  return bs.map((b) => {
    const q = (b.batch_quantity === null || b.batch_quantity === undefined || b.batch_quantity === "")
      ? "" : ` (${b.batch_quantity})`;
    return `${b.batch_number}${q}`;
  }).join(", ");
};

export default function Register() {
  const { user, can } = useAuth();
  const isAdmin = user?.role === "admin";
  const canReturns = isAdmin || can("returns", "edit");
  const [returnsFor, setReturnsFor] = useState(null);
  const [returnFilter, setReturnFilter] = useState("all");
  const [transportFilter, setTransportFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [batchQ, setBatchQ] = useState("");
  const [fromD, setFromD] = useState("");
  const [toD, setToD] = useState("");
  const [editing, setEditing] = useState(null); // dispatch
  const [reason, setReason] = useState("");
  const [changes, setChanges] = useState({});
  const [batches, setBatches] = useState([]);
  const [batchesTouched, setBatchesTouched] = useState(false);
  const [tab, setTab] = useState("register");

  const load = async () => {
    const [d, r] = await Promise.all([api.get("/dispatches"), api.get("/edit-requests")]);
    setRows(d.data);
    setRequests(r.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const bq = batchQ.trim().toLowerCase();
    return rows.filter((r) => {
      if (fromD && r.date < fromD) return false;
      if (toD && r.date > toD) return false;
      if (bq && !(r.batches || []).some((b) => (b.batch_number || "").toLowerCase().includes(bq))) return false;
      if (returnFilter !== "all" && r.return_type !== returnFilter) return false;
      if (transportFilter !== "all" && r.transport_group !== transportFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return [r.vehicle_number, r.customer_name, r.invoice_number, r.lr_gr_number, r.transporter_name, r.product, r.brand]
          .some((x) => (x || "").toLowerCase().includes(q))
          || (r.batches || []).some((b) => (b.batch_number || "").toLowerCase().includes(q));
      }
      return true;
    });
  }, [rows, search, fromD, toD, batchQ, returnFilter, transportFilter]);

  const openEdit = (row) => {
    setEditing(row);
    setReason("");
    setChanges({});
    // seed local batch editor with existing batches (or empty)
    const seed = (row.batches || []).map((b) => ({
      _id: uid(),
      batch_number: b.batch_number || "",
      batch_quantity: (b.batch_quantity === null || b.batch_quantity === undefined) ? "" : b.batch_quantity,
    }));
    setBatches(seed);
    setBatchesTouched(false);
  };

  const addBatchRow = () => { setBatches((a) => [...a, { _id: uid(), batch_number: "", batch_quantity: "" }]); setBatchesTouched(true); };
  const removeBatchRow = (id) => { setBatches((a) => a.filter((b) => b._id !== id)); setBatchesTouched(true); };
  const updateBatchRow = (id, patch) => { setBatches((a) => a.map((b) => b._id === id ? { ...b, ...patch } : b)); setBatchesTouched(true); };

  const editQty = useMemo(() => {
    if (!editing) return 0;
    const q = changes.quantity;
    return (q !== undefined && q !== null && q !== "" && !isNaN(q)) ? parseFloat(q) : parseFloat(editing.quantity || 0);
  }, [editing, changes.quantity]);

  const batchTotal = useMemo(() => batches.reduce((s, b) => s + (parseFloat(b.batch_quantity || 0) || 0), 0), [batches]);
  const batchFilledCount = batches.filter((b) => b.batch_quantity !== "" && b.batch_quantity !== null && b.batch_quantity !== undefined).length;
  const batchMismatch = batchFilledCount > 0 && Math.round(batchTotal * 10000) !== Math.round(editQty * 10000);
  const batchRemaining = editQty - batchTotal;

  const submitEdit = async () => {
    if (!reason.trim()) { toast.error("Reason is required."); return; }
    const clean = Object.fromEntries(Object.entries(changes).filter(([, v]) => v !== "" && v !== null && v !== undefined));

    // Batch changes: include only when user interacted with the batch editor
    if (batchesTouched) {
      for (const [i, b] of batches.entries()) {
        if (!(b.batch_number || "").trim()) { toast.error(`Batch #${i + 1}: batch number is required`); return; }
      }
      if (batchMismatch) {
        toast.error(`Sum of batch quantities (${batchTotal}) must equal Qty (${editQty}), or leave all batch quantities blank.`);
        return;
      }
      clean.batches = batches.map((b) => ({
        batch_number: (b.batch_number || "").trim(),
        batch_quantity: (b.batch_quantity === "" || b.batch_quantity === null || b.batch_quantity === undefined)
          ? null : parseFloat(b.batch_quantity),
      }));
    }

    if (Object.keys(clean).length === 0) { toast.error("No changes to submit."); return; }
    try {
      await api.post("/edit-requests", { dispatch_id: editing.id, changes: clean, reason });
      toast.success("Edit request sent to admin.");
      setEditing(null); load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    }
  };

  const decide = async (id, approve) => {
    try {
      await api.post(`/edit-requests/${id}/decision`, { approve, admin_note: "" });
      toast.success(approve ? "Approved & applied." : "Rejected.");
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    }
  };

  const pending = requests.filter((r) => r.status === "pending");

  const EXPORT_COLUMNS = [
    { k: "date", l: "Date", fmt: (v) => fmtDate(v) },
    { k: "vehicle_number", l: "Vehicle" },
    { k: "vehicle_type", l: "Type" },
    { k: "customer_name", l: "Customer" },
    { k: "invoice_number", l: "Invoice" },
    { k: "product", l: "Product" },
    { k: "brand", l: "Brand" },
    { k: "variant", l: "Variant" },
    { k: "packing", l: "Packing" },
    { k: "quantity", l: "Qty", fmt: (v) => num(v, 0) },
    { k: "weight_kg", l: "Weight (Kg)", fmt: (v) => kg(v) },
    { k: "batches", l: "# Batches", fmt: (v) => (v || []).length },
    { k: "batches", l: "Batches", fmt: (v) => batchesSummary(v) },
    { k: "transporter_name", l: "Transporter", fmt: (v, row) => row.vehicle_type === "customer" ? "— Not Applicable" : (v || "-") },
    { k: "approved_freight", l: "Freight", fmt: (v) => money(v) },
    { k: "lr_gr_number", l: "LR/GR" },
    { k: "pod", l: "POD" },
    { k: "delivery_date", l: "Delivery Date", fmt: (v) => (v ? fmtDate(v) : "-") },
    { k: "damaged_missing_qty", l: "D/M Qty", fmt: (v, row) => (row.pod === "Damaged" || row.pod === "Missing") ? num(v, 2) : "-" },
    { k: "transport_group", l: "Transport" },
    { k: "return_type", l: "Return Type" },
    { k: "last_return_date", l: "Return Date", fmt: (v) => (v ? fmtDate(v) : "-") },
    { k: "returned_qty", l: "Returned Qty", fmt: (v) => num(v, 2) },
    { k: "returned_weight_kg", l: "Returned Wt (Kg)", fmt: (v) => kg(v) },
    { k: "net_qty", l: "Net Qty (Retained for Cust. Transport)", fmt: (v) => num(v, 2) },
    { k: "net_weight_kg", l: "Net Wt (Retained for Cust. Transport)", fmt: (v) => kg(v) },
    { k: "consignment_status", l: "Consignment Status" },
  ];

  const exportFilename = () => {
    const parts = ["dispatch-register"];
    if (fromD) parts.push(`from-${fromD}`);
    if (toD) parts.push(`to-${toD}`);
    if (search) parts.push("filtered");
    if (batchQ) parts.push(`batch-${batchQ.trim().replace(/[^a-z0-9]+/gi, "_")}`);
    return parts.join("-");
  };

  const exportContext = () => {
    const lines = ["Dispatch Register"];
    lines.push(`Range: ${fromD ? fmtDate(fromD) : "—"} to ${toD ? fmtDate(toD) : "—"}`);
    if (search) lines.push(`Search: ${search}`);
    if (batchQ.trim()) lines.push(`Batch #: ${batchQ.trim()}`);
    lines.push(`Rows: ${filtered.length}`);
    return lines;
  };

  const buildTotals = () => {
    const totalWeight = filtered.reduce((s, r) => s + (parseFloat(r.weight_kg) || 0), 0);
    const totalFreight = filtered.reduce((s, r) => s + (parseFloat(r.approved_freight) || 0), 0);
    const totalQty = filtered.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0);
    const sum = (k) => filtered.reduce((s, r) => s + (parseFloat(r[k]) || 0), 0);
    return { totalQty, totalWeight, totalFreight, returned_qty: sum("returned_qty"), returned_weight_kg: sum("returned_weight_kg"), net_qty: sum("net_qty"), net_weight_kg: sum("net_weight_kg") };
  };

  const totalsCell = (c, totals) => {
    if (c.k === "date") return "TOTALS";
    if (c.k === "quantity") return num(totals.totalQty, 0);
    if (c.k === "weight_kg") return kg(totals.totalWeight);
    if (c.k === "approved_freight") return money(totals.totalFreight);
    if (c.k === "returned_qty" || c.k === "net_qty") return num(totals[c.k], 2);
    if (c.k === "returned_weight_kg" || c.k === "net_weight_kg") return kg(totals[c.k]);
    return "";
  };

  const exportExcel = () => {
    if (filtered.length === 0) { toast.error("No rows to export"); return; }
    const header = EXPORT_COLUMNS.map((c) => c.l);
    const body = filtered.map((r) => EXPORT_COLUMNS.map((c) => c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? "")));
    const totals = buildTotals();
    const totalsRow = EXPORT_COLUMNS.map((c) => totalsCell(c, totals));
    const meta = exportContext().map((line) => [line]);
    meta.push([]);
    const aoa = [...meta, header, ...body, [], totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Register");
    XLSX.writeFile(wb, `${exportFilename()}.xlsx`);
  };

  const exportPDF = () => {
    if (filtered.length === 0) { toast.error("No rows to export"); return; }
    const doc = new jsPDF({ orientation: "landscape", format: "a3" });
    const lines = exportContext();
    let y = 14;
    doc.setFontSize(14); doc.text(lines[0], 14, y); y += 6;
    doc.setFontSize(9);
    for (let i = 1; i < lines.length; i++) { doc.text(lines[i], 14, y); y += 4.5; }
    const totals = buildTotals();
    const totalsRow = EXPORT_COLUMNS.map((c) => totalsCell(c, totals));
    autoTable(doc, {
      startY: y + 2,
      head: [EXPORT_COLUMNS.map((c) => c.l)],
      body: filtered.map((r) => EXPORT_COLUMNS.map((c) => String(c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? "")))),
      foot: [totalsRow],
      styles: { fontSize: 6, cellPadding: 1.2, overflow: "linebreak" },
      headStyles: { fillColor: [37, 99, 235], fontSize: 7 },
      footStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold" },
      columnStyles: { 12: { cellWidth: 40 } }, // Batches column wider
    });
    doc.save(`${exportFilename()}.pdf`);
  };

  return (
    <div className="space-y-6" data-testid="register-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Dispatch Register</h1>
          <p className="text-slate-500 text-sm mt-1">All saved dispatches are locked. Request an edit for corrections. {!isAdmin && <span className="text-blue-700">Your edit requests remain pending until an Admin approves them.</span>}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab("register")} data-testid="tab-register" className={`px-4 py-2 text-sm font-medium rounded-md ${tab === "register" ? "bg-blue-600 text-white" : "bg-white border text-slate-700"}`}>Register ({rows.length})</button>
          <button onClick={() => setTab("approvals")} data-testid="tab-approvals" className={`px-4 py-2 text-sm font-medium rounded-md ${tab === "approvals" ? "bg-blue-600 text-white" : "bg-white border text-slate-700"}`}>
            Edit Requests {pending.length > 0 && <span className="ml-1 bg-amber-500 text-white rounded-full px-2 text-xs">{pending.length}</span>}
          </button>
        </div>
      </div>

      {tab === "register" && (
        <>
          <div className="flex gap-3 flex-wrap items-end">
            <div className="flex-1 min-w-[220px]"><Label>Search</Label><Input placeholder="Vehicle, customer, invoice, LR/GR, batch…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="register-search-input" /></div>
            <div className="min-w-[180px]">
              <Label>Batch #</Label>
              <div className="relative">
                <Input placeholder="Jump to batch…" value={batchQ} onChange={(e) => setBatchQ(e.target.value)} data-testid="register-batch-filter" className="pr-7" />
                {batchQ && (
                  <button type="button" onClick={() => setBatchQ("")} data-testid="register-batch-filter-clear"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs">✕</button>
                )}
              </div>
            </div>
            <div className="min-w-[150px]">
              <Label>Return Type</Label>
              <Select value={returnFilter} onValueChange={setReturnFilter}>
                <SelectTrigger data-testid="register-return-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="No Return">No Return</SelectItem>
                  <SelectItem value="Partial Return">Partial Return</SelectItem>
                  <SelectItem value="Full Return">Full Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[170px]">
              <Label>Transport</Label>
              <Select value={transportFilter} onValueChange={setTransportFilter}>
                <SelectTrigger data-testid="register-transport-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Company Transport">Company Transport</SelectItem>
                  <SelectItem value="Customer Transport">Customer Transport</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>From</Label><DateDMY value={fromD} onChange={setFromD} testid="register-from-date" /></div>
            <div><Label>To</Label><DateDMY value={toD} onChange={setToD} minDate={fromD} testid="register-to-date" /></div>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={exportExcel} disabled={filtered.length === 0} data-testid="register-export-excel">
                <Download className="h-4 w-4 mr-1.5" /> Excel
              </Button>
              <Button variant="outline" onClick={exportPDF} disabled={filtered.length === 0} data-testid="register-export-pdf">
                <FileText className="h-4 w-4 mr-1.5" /> PDF
              </Button>
            </div>
          </div>

          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  {["Date", "Vehicle", "Type", "Customer", "Invoice", "Product", "Brand", "Variant", "Packing", "Qty", "Weight", "# Batches", "Batches", "Transporter", "Freight", "LR/GR", "POD", "D/M Qty", "Return Type", "Return Date", "Ret. Qty", "Ret. Wt", "Net Qty", "Net Wt", "Status", "Action"].map((h) =>
                    <th key={h} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id} className="border-b last:border-b-0 hover:bg-slate-50" data-testid={`register-row-${i}`}>
                    <td className="px-3 py-2 font-mono-num whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{r.vehicle_number}</td>
                    <td className="px-3 py-2"><Badge className={typeBadge(r.vehicle_type)}>{r.vehicle_type}</Badge></td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.customer_name}</td>
                    <td className="px-3 py-2 font-mono-num">{r.invoice_number}</td>
                    <td className="px-3 py-2">{r.product}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.brand || "-"}</td>
                    <td className="px-3 py-2">{r.variant || "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.packing}</td>
                    <td className="px-3 py-2 font-mono-num text-right">{num(r.quantity, 0)}</td>
                    <td className="px-3 py-2 font-mono-num text-right">{kg(r.weight_kg)}</td>
                    <td className="px-3 py-2 font-mono-num text-right" data-testid={`register-num-batches-${i}`}>{(r.batches || []).length || "-"}</td>
                    <td className="px-3 py-2 text-xs max-w-[240px]" data-testid={`register-batches-${i}`}>
                      <span title={batchesSummary(r.batches)} className="line-clamp-2 break-words">{batchesSummary(r.batches)}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" data-testid={`register-transporter-${i}`}>
                      {r.vehicle_type === "customer" ? <span className="text-slate-400 italic">— Not Applicable</span> : (r.transporter_name || "-")}
                    </td>
                    <td className="px-3 py-2 font-mono-num text-right">{money(r.approved_freight)}</td>
                    <td className="px-3 py-2 font-mono-num">{r.lr_gr_number}</td>
                    <td className="px-3 py-2"><span className={`pill border ${podBadge(r.pod)}`}>{r.pod}</span></td>
                    <td className="px-3 py-2 font-mono-num text-right">{r.pod !== "Received" ? num(r.damaged_missing_qty, 2) : "-"}</td>
                    <td className="px-3 py-2"><span className={`pill border whitespace-nowrap ${returnBadge(r.return_type)}`} data-testid={`register-return-type-${i}`}>{r.return_type}</span></td>
                    <td className="px-3 py-2 font-mono-num whitespace-nowrap">{r.last_return_date ? fmtDate(r.last_return_date) : "-"}</td>
                    <td className="px-3 py-2 font-mono-num text-right" data-testid={`register-returned-qty-${i}`}>{r.returned_qty ? num(r.returned_qty, 2) : "-"}</td>
                    <td className="px-3 py-2 font-mono-num text-right whitespace-nowrap">{r.returned_weight_kg ? kg(r.returned_weight_kg) : "-"}</td>
                    <td className="px-3 py-2 font-mono-num text-right" data-testid={`register-net-qty-${i}`}>
                      {num(r.net_qty, 2)}{r.vehicle_type === "customer" && <div className="text-[10px] text-slate-400 font-sans">Retained</div>}
                    </td>
                    <td className="px-3 py-2 font-mono-num text-right whitespace-nowrap" data-testid={`register-net-weight-${i}`}>
                      {kg(r.net_weight_kg)}{r.vehicle_type === "customer" && <div className="text-[10px] text-slate-400 font-sans">Retained</div>}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`register-consignment-status-${i}`}>{r.consignment_status}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setReturnsFor(r)} data-testid={`returns-btn-${i}`}>
                          <Undo2 className="h-3.5 w-3.5 mr-1" /> Returns
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`request-edit-btn-${i}`}>
                          <Lock className="h-3.5 w-3.5 mr-1" /> Request Edit
                        </Button>
                        {user?.role === "admin" && (
                          <DeleteConfirm url={`/dispatches/${r.id}`}
                            title="Delete Dispatch"
                            description={<div>Delete dispatch line <b>{r.invoice_number}</b> for <b>{r.customer_name}</b> ({[r.product, r.brand, r.packing].filter(Boolean).join(" · ")} × {r.quantity})? Its POD photo and pending edit requests will also be removed.</div>}
                            onDeleted={load}
                            testid={`delete-dispatch-${i}`} iconOnly />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="26" className="text-center text-slate-500 py-8">No dispatches found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "approvals" && (
        <div className="table-wrap">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
              <tr>
                {["Requested", "Requested By", "Dispatch", "Changes", "Reason", "Status", "Decided By", "Action"].map((h) => <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {requests.map((r, i) => {
                const disp = rows.find((d) => d.id === r.dispatch_id);
                return (
                  <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`approval-row-${i}`}>
                    <td className="px-3 py-2 font-mono-num text-xs">{new Date(r.requested_at).toLocaleString()}</td>
                    <td className="px-3 py-2">{r.requested_by_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap" data-testid={`approval-dispatch-${i}`}>
                      {disp ? (
                        <div>
                          <div className="font-mono-num text-slate-800">{fmtDate(disp.date)}</div>
                          <div className="text-xs text-slate-600">{disp.customer_name || "—"}</div>
                        </div>
                      ) : <span className="text-slate-400 font-mono-num text-xs">{r.dispatch_id.slice(0, 8)}</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {Object.entries(r.changes).map(([k, v]) => {
                        let display;
                        if (k === "batches" && Array.isArray(v)) {
                          display = batchesSummary(v);
                        } else {
                          display = String(v);
                        }
                        return (<div key={k}><b>{k}</b>: {display}</div>);
                      })}
                    </td>
                    <td className="px-3 py-2 text-xs max-w-xs">{r.reason}</td>
                    <td className="px-3 py-2"><Badge className={r.status === "pending" ? "bg-amber-100 text-amber-800" : r.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}>{r.status}</Badge></td>
                    <td className="px-3 py-2 text-xs" data-testid={`approval-decided-by-${i}`}>
                      {r.decided_by_name ? (
                        <div>
                          <div className="font-medium text-slate-800">{r.decided_by_name}</div>
                          {r.decided_at && <div className="text-slate-500 font-mono-num">{new Date(r.decided_at).toLocaleString()}</div>}
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {isAdmin && r.status === "pending" ? (
                        <div className="flex gap-1">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(r.id, true)} data-testid={`approve-btn-${i}`}><Check className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="destructive" onClick={() => decide(r.id, false)} data-testid={`reject-btn-${i}`}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        !isAdmin && r.status === "pending" ? <span className="text-xs text-slate-500 italic">Awaiting Admin</span> : null
                      )}
                    </td>
                  </tr>
                );
              })}
              {requests.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-slate-500">No edit requests.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <ReturnsDialog dispatch={returnsFor} canEdit={canReturns} onClose={() => setReturnsFor(null)} onChanged={load} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PencilLine className="h-4 w-4" /> Request Edit</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded-md">
                <b>{editing.vehicle_number}</b> / {editing.customer_name} / {editing.invoice_number}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["invoice_number", "Invoice Number"],
                  ["lr_gr_number", "LR/GR Number"],
                  ["quantity", "Quantity", "number"],
                  ["approved_freight", "Approved Freight", "number"],
                  ["pod", "POD"],
                  ["damaged_missing_qty", "Damaged/Missing Qty", "number"],
                ].map(([k, l, type]) => (
                  <div key={k}>
                    <Label className="text-xs">{l}</Label>
                    <Input type={type || "text"} placeholder={String(editing[k] ?? "")}
                      value={changes[k] ?? ""} onChange={(e) => setChanges((s) => ({ ...s, [k]: type === "number" ? parseFloat(e.target.value) : (k === "invoice_number" ? e.target.value.toUpperCase() : e.target.value) }))}
                      data-testid={`edit-req-${k}-input`} />
                  </div>
                ))}
              </div>

              {/* Batches editor */}
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                    <Layers className="h-4 w-4" /> Batches
                  </div>
                  <div className="text-[11px] font-mono-num text-slate-500">
                    Qty: <b>{editQty || 0}</b>
                    {batchFilledCount > 0 && (
                      <>
                        {" · "}Total: <b className={batchMismatch ? "text-rose-600" : "text-slate-700"}>{batchTotal || 0}</b>
                        {" · "}Remaining: <b className={batchRemaining < 0 ? "text-rose-600" : "text-slate-700"}>{isNaN(batchRemaining) ? 0 : batchRemaining}</b>
                      </>
                    )}
                  </div>
                </div>
                {batches.length === 0 && (
                  <div className="text-xs text-slate-500 mb-2">
                    {editing?.batches?.length ? "All batches removed." : "No batches recorded. Add one below to include batch info in this edit request."}
                  </div>
                )}
                <div className="space-y-2">
                  {batches.map((b, bi) => (
                    <div key={b._id} className="grid grid-cols-12 gap-2 items-end" data-testid={`edit-batch-row-${bi}`}>
                      <div className="col-span-5">
                        <Label className="text-[11px]">Batch #{bi + 1} Number *</Label>
                        <Input value={b.batch_number || ""}
                          onChange={(e) => updateBatchRow(b._id, { batch_number: e.target.value.toUpperCase() })}
                          placeholder="e.g. BATCH-001"
                          className="uppercase"
                          data-testid={`edit-batch-number-${bi}`} />
                      </div>
                      <div className="col-span-6">
                        <Label className="text-[11px]">Quantity (optional)</Label>
                        <Input type="number" min="0" step="0.01" value={b.batch_quantity ?? ""}
                          onChange={(e) => updateBatchRow(b._id, { batch_quantity: e.target.value })}
                          placeholder="Leave blank if unknown"
                          data-testid={`edit-batch-quantity-${bi}`} />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button variant="ghost" size="icon" onClick={() => removeBatchRow(b._id)} data-testid={`edit-batch-remove-${bi}`}>
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addBatchRow} className="mt-2 border-dashed" data-testid="edit-batch-add">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Batch
                </Button>
                {batchMismatch && (
                  <div className="mt-2 text-[11px] text-rose-600">
                    Sum of batch quantities must equal Qty ({editQty}) or leave all batch quantities blank.
                  </div>
                )}
                <p className="mt-2 text-[11px] text-slate-500">Batch changes are included in the request and recorded in the Audit Log after admin approval.</p>
              </div>

              <div>
                <Label>Reason for Change *</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="edit-req-reason" placeholder="Explain why this change is needed…" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitEdit} data-testid="submit-edit-request">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
